import {
    FileFormatDetailModelDto,
    FileFormatFieldGroupModelDto,
    FileFormatFieldModelDto,
    FileFormatModelDto,
    FileImportLayoutFieldModelDto,
} from '@treasury/api/channel';

export interface Tile {
    id: number;
    order?: number | undefined;
    beginPosition?: number | undefined;
    endPosition?: number | undefined;
    width: number;
    type: string;
    name: string;
    active?: string;
    optional?: string;
    required?: string;
    text?: string;
    additionalInformationOne?: string;
    additionalInformationTwo?: string;
}

export type FillerTile = {
    id: number;
    order: number | undefined;
    beginPosition: number | undefined;
    endPosition: number | undefined;
    width: number;
    type: string;
    name: string;
    active: string;
    optional: string;
    required: string;
};

export type GlossaryTerm = {
    name: string;
    text: string;
};

export type GlossaryGroup = {
    title: string;
    label: string;
    id: string;
    terms: GlossaryTerm[];
};

export type TilesGroup = Array<{
    id: number;
    width: number;
    type: string;
    name: string;
    text: string;
    optional: string;
    active: string;
}>;

export type WireUploadFormatUIModelReturn = {
    id: number;
    title: string;
    type: string;
    name: string;
    delimiter: string;
    excludeHeaderRows: number;
    excludeFooterRows: number;
    itemAmountFormat: string;
    effectiveDateFormat: string | undefined;
    containers: Array<{ id: string; title: string; active: string }>;
    tiles: Tile[];
};

export const fillerTile: Tile = {
    id: 100,
    order: undefined,
    beginPosition: undefined,
    endPosition: undefined,
    width: 1,
    type: 'filler',
    name: 'Filler Tile',
    active: 'true',
    optional: 'true',
    required: 'false',
};

export const getItemAmountFormat = (tile: FileFormatDetailModelDto) => {
    if (tile.additionalInformationOne?.toLowerCase() === 'decimalincluded') {
        return 'DecimalIncluded';
    }
    if (tile.additionalInformationTwo) {
        return tile.additionalInformationTwo;
    }
    return 'DecimalIncluded';
};

interface BuildAndSortTilesResult {
    activeTiles: Tile[];
    inactiveTiles: Tile[];
}

interface Field {
    id: number;
    width: number;
    type: string;
    name: string;
    text: string;
    optional: string;
    active: string;
    order?: number | undefined;
    beginPosition?: number | undefined;
    endPosition?: number | undefined;
}

export type FieldsArray = Field[][] | Field[];

export const buildAndSortTiles = (
    { fileFormatDetails }: FileFormatModelDto,
    fields: FieldsArray
): BuildAndSortTilesResult => {
    const compareText = (a: string, b: string) => a.toLowerCase().trim() === b.toLowerCase().trim();
    const getUsedField = ({ text }: Field): FileFormatDetailModelDto | undefined =>
        fileFormatDetails?.find(({ fileFormatField }) =>
            compareText(fileFormatField as string, text)
        );

    const activeTiles: Tile[] = (fields as Field[])
        .flat()
        .filter(getUsedField)
        .map((f: Field) => {
            const usedField = getUsedField(f) as FileFormatDetailModelDto;
            const { beginPosition, endPosition, order, id } = usedField;
            return {
                ...f,
                width: (endPosition as number) - (beginPosition as number) + 1 || 1,
                beginPosition,
                endPosition,
                id,
                active: 'true',
                order,
            };
        })
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        .sort((a, b) => (a.beginPosition ?? 0) - (b.beginPosition ?? 0));

    const inactiveTiles: Tile[] = (fields as Field[])
        .flat()
        .filter((f: Field) => !getUsedField(f))
        .map((f: Field) => ({
            ...f,
            order: undefined,
            beginPosition: undefined,
            endPosition: undefined,
        }));

    return { activeTiles, inactiveTiles };
};

/**
 *
 * @param {string} formatType fileFormatType property from the service response: 'FixedPosition', 'Delimited'
 * @param {Array} tiles any tiles with {active: 'true'}
 * @returns array of tiles with filler tiles placed in index or range gaps
 */
export const addFillerTiles = (formatType: string, tiles: Tile[]): Tile[] => {
    const tilesAreFixed = formatType === 'FixedPosition';

    /**
     * @returns the tile in the data that should go in the slot position || undefined
     */
    const mapTileToSlot = (slot: number, i: number) => {
        // Service data uses 1 for 0 index position
        const dbIndex = i + 1;

        const tileForSlot = tilesAreFixed
            ? tiles.find(
                  ({ beginPosition, endPosition }: Tile) =>
                      beginPosition !== undefined &&
                      endPosition !== undefined &&
                      beginPosition <= dbIndex &&
                      endPosition >= dbIndex
              )
            : tiles.find(({ order }) => order === dbIndex);

        return tileForSlot || undefined;
    };

    // Any slots still undefined after adding tiles should be filled with filler tiles
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const mapFillerTilesToEmptySlots = (s: any) => s ?? fillerTile;

    const maxTileOrder = Math.max(...tiles.map(({ order }) => (order ? +order : 0)));
    const maxTileRangePosition = Math.max(
        ...tiles.map(({ endPosition }) => (endPosition ? +endPosition : 0))
    );

    // Use highest order or range value to set length of the empty slot array
    const emptySlots = new Array(tilesAreFixed ? maxTileRangePosition : maxTileOrder).fill(
        undefined
    );
    if (tilesAreFixed) {
        // apply a copy of each item to every slot in its range
        const condensedRangeSlots = emptySlots
            .map(mapTileToSlot)
            .map(mapFillerTilesToEmptySlots)
            .reduce((acc, slot) => {
                const previousSlot = acc[acc.length - 1];
                const previousIsSameType = previousSlot && previousSlot.id === slot.id;

                return !acc.length || !previousIsSameType ? [...acc, slot] : acc;
            }, []);

        // use previous and next item tiles to set filler beginPosition and endPosition values
        return condensedRangeSlots.map((slot: { type: string }, i: number) => {
            if (slot.type !== 'filler') {
                return slot;
            }

            const prevSlot = condensedRangeSlots[i - 1];
            const nextSlot = condensedRangeSlots[i + 1];

            const beginPosition = prevSlot ? prevSlot.endPosition + 1 : 1;
            const endPosition = nextSlot.beginPosition - 1;

            const width = endPosition - beginPosition + 1;

            return { ...slot, beginPosition, endPosition, width };
        });
    }
    return emptySlots
        .map(mapTileToSlot)
        .map(mapFillerTilesToEmptySlots)
        .map((slot, i) => ({ ...slot, order: i + 1 }));
};

export interface WireUploadFormatUIModel {
    id: number;
    title: string;
    type: string;
    name: string;
    delimiter: string;
    excludeHeaderRows: number;
    excludeFooterRows: number;
    itemAmountFormat: string;
    effectiveDateFormat: string;
    containers: Array<{ id: string; title: string; active: string }>;
    tiles: Tile[];
}

export const mapResponseToWireUploadFormat = (res: FileFormatModelDto, fields: FieldsArray) => {
    const effectiveTile = res.fileFormatDetails?.find(
        tile => tile.fileFormatField === 'WireEffectiveDate'
    );
    const amountTile = res.fileFormatDetails?.find(tile => tile.fileFormatField === 'WireAmount');
    const effectiveDateFormat = effectiveTile?.additionalInformationOne;
    const itemAmountFormat = getItemAmountFormat(amountTile as FileFormatDetailModelDto);

    const { activeTiles, inactiveTiles } = buildAndSortTiles(res, fields);

    return {
        id: res.id,
        title: 'Wire Upload Template Formatting Tool',
        type: res.fileFormatType,
        name: res.templateName,
        delimiter: res.fieldDelimiter,
        excludeHeaderRows: res.headerRows || 0,
        excludeFooterRows: res.footerRows || 0,
        itemAmountFormat,
        effectiveDateFormat,
        containers: [
            { id: 'in', title: 'Drag and drop items to reorder columns', active: 'true' },
            {
                id: 'out',
                title: 'Drag and drop optional items to the space above to activate',
                active: 'false',
            },
        ],
        tiles: [
            ...addFillerTiles(res.fileFormatType as string, activeTiles),
            ...inactiveTiles,
            {
                id: 100,
                order: undefined,
                beginPosition: undefined,
                endPosition: undefined,
                width: 1,
                type: 'filler',
                name: 'Filler Tile',
                optional: 'true',
                required: 'false',
            } as FillerTile,
        ],
    };
};

/**
 *
 * @param {*} params id, fileFormatModel
 * * NOTES ON CRAZINESS:
 * - ADDITIONAL INFORMATION FIELDS (while mapping tiles to fields and matching active tiles):
 *      - when the field is EffectiveDate: additionalInformationOne should hold the effectiveDateFormat
 *      - when the field is WireAmount: additionalInformationOne is the itemAmountFormat ONLY when it's decimalIncluded,
 *          ELSE additionalInformationOne must be 'decimalNotIncluded' and additionalInformationTwo must be the itemAmountFormat (wholeDollar or impliedDecimal)
 */
export const mapRequestToUploadFormatParameters = (params: {
    id?: number;
    name: string;
    type: string;
    delimiter?: string;
    excludeHeaderRows?: number | string;
    excludeFooterRows?: number | string;
    tiles: Tile[];
    effectiveDateFormat?: string;
    itemAmountFormat?: string;
}): FileFormatModelDto => ({
    id: params.id || 0,
    templateName: params.name,
    fileFormatType: params.type,
    fieldDelimiter: params.delimiter || ',',
    headerRows: Number(params.excludeHeaderRows),
    footerRows: Number(params.excludeFooterRows),
    updatedDate: '', // Add a default or appropriate value for updatedDate
    fileFormatDetails: params.tiles
        .filter(tile => tile.active === 'true' && tile.type !== 'filler')
        .map((tile, idx) => ({
            id: tile.id ? Number(tile.id) : 0,
            fileFormatField: tile.text ?? '',
            order: Number(tile.order),
            beginPosition: Number(tile.beginPosition) || idx,
            endPosition: Number(tile.endPosition) || idx,
            additionalInformationOne:
                // eslint-disable-next-line no-nested-ternary
                tile.text === 'WireEffectiveDate'
                    ? params.effectiveDateFormat
                    : tile.text === 'WireAmount' && params.itemAmountFormat === 'DecimalIncluded'
                      ? params.itemAmountFormat
                      : 'DecimalNotIncluded',
            additionalInformationTwo:
                tile.text === 'WireAmount' && params.itemAmountFormat !== 'DecimalIncluded'
                    ? params.itemAmountFormat
                    : undefined,
            additionalInformationThree: undefined,
            additionalInformationFour: undefined,
            updatedDate: '',
            updatedBy: '',
        })),
});

export const mapResponseToGlossaryAndTiles = (res: FileImportLayoutFieldModelDto) => {
    const glossary = res.fieldGroups?.map((group: FileFormatFieldGroupModelDto) => ({
        title: group.info,
        label: group.info,
        id: group.name,
        terms: group.fields?.map((field: FileFormatFieldModelDto) => ({
            name: field.description,
            text: field.info,
        })),
    })) as GlossaryGroup[];

    const tiles = res.fieldGroups?.map((group: FileFormatFieldGroupModelDto) =>
        group.fields?.map((field: FileFormatFieldModelDto) => ({
            id: field.id,
            width: 1,
            type: group.description?.toLowerCase(),
            name: field.description,
            text: field.name,
            optional: (!field.required).toString(),
            active: field.required ? 'true' : 'false',
        }))
    ) as TilesGroup[];
    return {
        glossary,
        tiles,
    };
};
